/**
 * School directory — admin page. The platform-curated registry of real schools
 * and the classes inside them, whether or not they are customers.
 *
 * Two things this page is careful about, both from
 * docs/FeatureDesigns/school-teachers-parents/schoolclass-model-target.md:
 *
 *   - **Duplicates are expected** (SC-6a, merging is unsupported). Search helps
 *     someone find an existing record before adding another; it does not block one.
 *   - **Moving a record between the directory and a tenant is a separate act**
 *     from editing. Renaming must never move a
 *     school between tenants, so ownership has its own control.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BackButton, Button, DataTable, Input, Modal, Select, SearchFilterBar,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { ALL_SUBJECT_OPTIONS, GRADE_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS } from '@padcanvas/shared';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { authApi } from '../api/auth';
import { effectiveRoleNames } from '../config/adminRoles';
import { schoolsApi, type School, type SchoolClass } from '../api/schoolsApi';
import { PUBLIC_TENANT_ID } from '../api/tenantsApi';
import './Schools.css';

/** A school's boards, tolerating a row still on the retired singular field.
 *  Mirrors `schoolBoards` on the backend. */
/** Subjects a class can teach. The content catalogue, not the caller's own —
 *  a class teaches what it teaches regardless of who is looking. */
/**
 * What a class is assumed to teach when it has recorded nothing.
 *
 * Derived from the canonical subject list rather than typed out: the hand-written
 * version had drifted and was missing hindi and both English subjects, so a class
 * with no stored subjects could never be given an English plan — the subject was
 * simply absent from every picker that fell back to here.
 *
 * `ALL_SUBJECT_OPTIONS` is the single source of truth for curriculum and
 * revision-plan surfaces, which is exactly what these pickers are.
 */
export const CLASS_SUBJECTS: string[] = [...ALL_SUBJECT_OPTIONS];

export function boardsOf(s: Pick<School, 'boards' | 'board'>): string[] {
  return s.boards?.length ? s.boards : (s.board ? [s.board] : []);
}

function fmtWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Academic year runs Apr–Mar, so a January date still belongs to the year that
 *  started the previous April. */
function currentAcademicYear(): string {
  const now = new Date();
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** A public record is the normal state for most of the directory — the majority
 *  of schools are there and never belong to a customer — so it reads neutral
 *  rather than as something unfinished. */
export function OwnerTag({ tenantId }: { tenantId: string }) {
  const isPublic = !tenantId || tenantId === PUBLIC_TENANT_ID;
  return (
    <span className={isPublic ? 'school-public' : 'school-tenant-owned'}>
      {isPublic ? 'public directory' : tenantId}
    </span>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────

/**
 * The school itself — name, city, boards and ownership, all on one tab.
 *
 * These were three places: a "Rename" modal, a standalone Ownership block, and
 * a boards field only reachable through the modal. One form is fewer places to
 * look and makes it obvious that a claim is NOT part of an edit.
 *
 * Claiming stays its own control and its own call. A school signing up does not
 * claim its record (SC-6a), and ownership must never move as a side effect of
 * fixing a typo in the name.
 */
function SchoolGeneralTab({ school, onDeleted }: { school: School; onDeleted?: () => void }) {
  const qc = useQueryClient();
  // Curating an existing directory record is the platform's job (§4.1.2): a
  // school's name, city and boards are read by everyone who finds it, and a
  // wrong one is everyone's problem. CREATING a class is deliberately NOT
  // gated this way (SC-5) — a student who cannot find their own class must be
  // able to add it, which is why the classes table below stays editable.
  const user = authApi.getUser();
  const canEdit = !!user?.isSuperAdmin || effectiveRoleNames(user).includes('content_admin');
  const [name, setName] = useState(school.name);
  const [city, setCity] = useState(school.city ?? '');
  const [boards, setBoards] = useState<string[]>(boardsOf(school));

  const dirty = name !== school.name
    || city !== (school.city ?? '')
    || boards.slice().sort().join() !== boardsOf(school).slice().sort().join();


  /**
   * `saveSchool` ignores `tenantId` on purpose: a claim must never be a side
   * effect of an edit, so there is no path by which renaming a school can
   * quietly transfer it (SC-6a). Ownership is no longer editable here at all —
   * the header still says who holds it — so this form issues one call and can
   * never re-stamp the school's classes as a byproduct.
   */
  /**
   * Removing a directory record.
   *
   * The directory is created by ANYONE (SC-5), which is the right rule and which
   * means it accumulates: a school typed twice, and one per end-to-end test run.
   * There was no way to remove any of them except a script against Cosmos.
   *
   * The server decides what is removable — unclaimed, and nothing real underneath
   * — and names what is in the way when it refuses. This is only the door.
   */
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => schoolsApi.deleteSchool(school.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      onDeleted?.();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      await schoolsApi.saveSchool({
        id: school.id, name: name.trim(), city: city.trim() || undefined, boards,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      qc.invalidateQueries({ queryKey: ['school-classes', school.id] });
    },
  });

  return (
    <section className="school-section">
      {!canEdit && (
        <p className="school-hint">
          The directory is curated by the platform, so these are read-only for you. Adding a
          class is not — that is the table below.
        </p>
      )}

      <label className="school-field">
        Name
        <Input value={name} disabled={!canEdit} onChange={e => setName(e.target.value)} />
      </label>
      <label className="school-field">
        City
        <Input value={city} disabled={!canEdit} placeholder="Pune"
          onChange={e => setCity(e.target.value)} />
      </label>

      {/* PLURAL: plenty of schools run CBSE and ICSE side by side. Which board a
          given CLASS follows is set on the class — this is only what the school
          offers, and it constrains that choice. */}
      <fieldset className="school-boards">
        <legend>Boards offered</legend>
        {REGISTRABLE_PROGRAMME_OPTIONS.map(b => {
          const on = boards.includes(b);
          return (
            <label key={b} className={on ? 'is-on' : ''}>
              <input type="checkbox" checked={on} disabled={!canEdit}
                onChange={() => setBoards(cur => (on ? cur.filter(x => x !== b) : [...cur, b]))} />
              {b}
            </label>
          );
        })}
      </fieldset>

      {/* A school records no grades of its own \u2014 deliberately, for now. The
          classes show which grades have been ADDED, which is not the same as
          which the school runs; if that gap ever needs closing it wants its own
          field rather than being inferred from the rows that happen to exist. */}

      {canEdit && (
        <>
          <div className="school-row">
            <Button variant="primary" size="sm" disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}>
              {save.isPending ? 'Saving\u2026' : 'Save'}
            </Button>
            {save.isSuccess && !dirty && <span className="school-hint">Saved.</span>}
          </div>
          {save.isError && <p className="school-error">{(save.error as Error).message}</p>}

          {/* Apart from Save, and last: it is the one control here that cannot be
              undone by editing the field back. */}
          <div className="school-danger">
            {confirmDelete ? (
              <>
                <span className="school-hint">
                  Delete <b>{school.name}</b>? Its empty classes and sections go with it.
                </span>
                <Button variant="secondary" size="sm" disabled={remove.isPending}
                  onClick={() => setConfirmDelete(false)}>Cancel</Button>
                <Button variant="danger" size="sm" disabled={remove.isPending}
                  onClick={() => remove.mutate()}>
                  {remove.isPending ? 'Deleting…' : 'Delete school'}
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(true)}>
                Delete school
              </Button>
            )}
          </div>
          {/* The server refuses with a list of what is in the way, one per line. */}
          {remove.isError && (
            <p className="school-error school-error--multiline">{(remove.error as Error).message}</p>
          )}
        </>
      )}
    </section>
  );
}


function ClassesSection({ school }: { school: School }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // A subject's internal name is a key, not a label.
  const subjectLabel = useSubjectLabel();
  // Alphabetical by the label shown, not by the internal key — the picker is
  // read, so it should order the way it reads.
  const sortedSubjects = useMemo(
    () => [...CLASS_SUBJECTS].sort((a, b) => subjectLabel(a).localeCompare(subjectLabel(b))),
    [subjectLabel],
  );
  const [modal, setModal] = useState<Partial<SchoolClass> | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  // Section ascending by default — the order a person scanning for "10-B"
  // expects, rather than insertion order.
  const [sort, setSort] = useState<SortState | undefined>({ sortBy: 'section', sortOrder: 'asc' });

  const { data, isLoading } = useQuery({
    queryKey: ['school-classes', school.id],
    queryFn: () => schoolsApi.listClasses(school.id),
  });

  const all = useMemo(() => data?.classes ?? [], [data]);

  /** Year and grade come from the DATA, not a fixed list — a school with three
   *  years should not offer twelve, and a filter for a value nothing has is a
   *  dead end. */
  const filterDefs = useMemo<FilterDef[]>(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))];
    return [
      {
        key: 'academicYear',
        placeholder: 'All years',
        options: uniq(all.map(c => c.academicYear)).sort().reverse()
          .map(v => ({ value: v, label: v })),
      },
      {
        key: 'grade',
        placeholder: 'All grades',
        options: uniq(all.map(c => c.grade))
          .sort((a, b) => Number(a) - Number(b))
          .map(v => ({ value: v, label: `Grade ${v}` })),
      },
    ];
  }, [all]);

  const rows = useMemo(() => {
    const term = (filters.search ?? '').trim().toLowerCase();
    return all.filter(c => {
      if (filters.academicYear && c.academicYear !== filters.academicYear) return false;
      if (filters.grade && c.grade !== filters.grade) return false;
      if (!term) return true;
      // Searches what a person would type: the grade, the year, the board.
      return [c.grade, c.academicYear, c.board, `Grade ${c.grade}`]
        .some(v => (v ?? '').toLowerCase().includes(term));
    });
  }, [all, filters]);

  const save = useMutation({
    mutationFn: (c: Partial<SchoolClass>) => schoolsApi.saveClass(school.id, c),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-classes', school.id] });
      setModal(null);
    },
  });

  // Grade leads: a class IS a grade. Its sections are records under it, listed on the
  // class's own page rather than here, where they would turn one row per grade back into
  // one row per section — which is the shape this model exists to undo.
  const columns: ColumnDef<SchoolClass>[] = [
    { key: 'grade', header: 'Grade', sortable: true, sortValue: c => Number(c.grade) || 0, render: c => c.grade },
    { key: 'academicYear', header: 'Year', sortable: true, sortValue: c => c.academicYear, render: c => c.academicYear },
    { key: 'board', header: 'Board', sortable: true, sortValue: c => c.board || '', render: c => c.board || '—' },
    { key: 'tenantId', header: 'Owner', sortable: true, sortValue: c => c.tenantId, render: c => <OwnerTag tenantId={c.tenantId} /> },
    { key: 'createdAt', header: 'Added', sortable: true, sortValue: c => c.createdAt, render: c => fmtWhen(c.createdAt) },
    // Who put it here. A class can be added by a student who could not find
    // theirs (SC-5), so this is often not an admin — and knowing which is the
    // difference between a curation slip and a real class.
    {
      key: 'createdBy',
      header: 'Added by',
      sortable: true,
      // Sorts by what is SHOWN, so the column orders the way it reads.
      sortValue: c => (c.createdByName ?? c.createdByEmail ?? c.createdBy ?? '').toLowerCase(),
      // A guid tells an admin nothing. Name first, email second, and the raw id
      // only when the account behind it is gone — still an answer, and better
      // than a dash that reads as "nobody added this".
      render: c => (c.createdByName || c.createdByEmail
        ? (
          <span title={c.createdByEmail ?? c.createdBy}>
            {c.createdByName || c.createdByEmail}
          </span>
        )
        : <span className="school-hint" title={c.createdBy}>{c.createdBy ? 'unknown user' : '—'}</span>),
    },
  ];

  return (
    <section className="school-section">
      <div className="school-section-head">
        <h2>Classes</h2>
        <Button variant="primary" size="sm"
          onClick={() => setModal({ academicYear: currentAcademicYear(), grade: '10' })}>
          + New Class
        </Button>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search section, grade, year…"
        hideFetch
      />

      <DataTable<SchoolClass>
        columns={columns}
        data={rows}
        rowKey={c => c.id}
        loading={isLoading}
        bordered
        sort={sort}
        onSortChange={setSort}
        // The row is the way in: students, exams, papers and curriculum all
        // hang off a class, and a modal per kind could show none of them
        // together and could not be linked to.
        onRowClick={c => navigate(`/schools/${school.id}/classes/${c.id}`)}
        emptyMessage={
          all.length === 0
            ? 'No classes recorded for this school yet.'
            : 'No classes match those filters.'
        }
      />

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title="New Class" size="sm">
        {modal && (
          <div className="school-form">
            <label>Academic year
              <Input value={modal.academicYear ?? ''} placeholder="2026-27"
                onChange={e => setModal({ ...modal, academicYear: e.target.value })} />
            </label>
            <label>Grade
              <Select value={modal.grade ?? '10'}
                onChange={e => setModal({ ...modal, grade: e.target.value })}>
                {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
              </Select>
            </label>
            {/* Which of the SCHOOL's boards this class follows — the field a
                shared curriculum resolves from. Constrained to what the school
                offers, so a class cannot sit on a board nobody there teaches. */}
            <label>Board
              <Select value={modal.board ?? ''}
                onChange={e => setModal({ ...modal, board: e.target.value || undefined })}>
                <option value="">
                  {boardsOf(school).length ? '— pick one —' : '— school has no boards set —'}
                </option>
                {boardsOf(school).map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
            </label>
            {/* A class is per-year, so the same grade in a new year is a new class —
                and repeating an existing tuple updates it. */}
            {/* With board + grade, this is the class's curricular identity —
                one curriculum per subject, resolved once for the room instead
                of per student. It is also what every subject picker hanging off
                the class should offer. */}
            <fieldset className="school-boards school-boards--grid">
              <legend>Subjects taught</legend>
              {sortedSubjects.map(sub => {
                const on = (modal.subjects ?? []).includes(sub);
                return (
                  <label key={sub} className={on ? 'is-on' : ''}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => setModal({
                        ...modal,
                        subjects: on
                          ? (modal.subjects ?? []).filter(x => x !== sub)
                          : [...(modal.subjects ?? []), sub],
                      })}
                    />
                    {subjectLabel(sub)}
                  </label>
                );
              })}
            </fieldset>
            {save.isError && <p className="school-error">{(save.error as Error).message}</p>}
            <div className="school-modal-actions">
              <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="primary" size="sm"
                disabled={!modal.academicYear?.trim() || save.isPending}
                onClick={() => save.mutate(modal)}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function Schools() {
  const qc = useQueryClient();
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [selected, setSelected] = useState<School | null>(null);
  const [modal, setModal] = useState<Partial<School> | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['schools', submitted],
    queryFn: () => schoolsApi.listSchools(submitted ? { search: submitted } : undefined),
  });

  const save = useMutation({
    mutationFn: (s: Partial<School>) => schoolsApi.saveSchool(s),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['schools'] }); setModal(null); },
  });

  // The server scopes this list to what the caller administers — a tenant's
  // admin sees their own schools and nothing else. Owner is therefore the same
  // value on every row for them, and only tells a super-admin anything.
  const isSuperAdmin = authApi.getUser()?.isSuperAdmin === true;
  const columns: ColumnDef<School>[] = [
    { key: 'name', header: 'School', render: s => s.name },
    { key: 'city', header: 'City', render: s => s.city || '—' },
    { key: 'boards', header: 'Boards', render: s => boardsOf(s).join(', ') || '—' },
    ...(isSuperAdmin
      ? [{ key: 'tenantId', header: 'Owner', render: (s: School) => <OwnerTag tenantId={s.tenantId} /> } satisfies ColumnDef<School>]
      : []),
    { key: 'createdAt', header: 'Added', render: s => fmtWhen(s.createdAt) },
  ];

  if (selected) {
    return (
      // Same shell and tab strip as the class page beneath it, so drilling from
      // school to class does not change how the page works.
      <div className="submissions-page">
        <BackButton onClick={() => setSelected(null)} label="Schools" />
        <div className="page-header">
          <h1>{selected.name}</h1>
          <p className="school-hint">
            {selected.city ? `${selected.city} · ` : ''}
            {boardsOf(selected).join(', ') || 'no boards recorded'}
            {' · '}
            {selected.tenantId === PUBLIC_TENANT_ID ? 'public directory' : `tenant ${selected.tenantId}`}
          </p>
        </div>

        {/* One page, not two tabs. A school is four fields; hiding its classes
            behind a tab made the reader click to reach the only part of the
            record with any depth. */}
        <SchoolGeneralTab school={selected} onDeleted={() => setSelected(null)} />
        <ClassesSection school={selected} />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <h1>Schools</h1>
        <Button variant="primary" size="sm" onClick={() => setModal({})}>+ New School</Button>
      </div>

      <div className="school-row school-search">
        <Input
          value={term}
          onChange={e => setTerm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') setSubmitted(term.trim()); }}
          placeholder="Search by name…"
        />
        <Button variant="secondary" size="sm" onClick={() => setSubmitted(term.trim())}>Search</Button>
        {submitted && (
          <Button variant="secondary" size="sm"
            onClick={() => { setTerm(''); setSubmitted(''); }}>Clear</Button>
        )}
      </div>

      {error && <div className="school-error">{(error as Error).message}</div>}

      {!error && (
        <DataTable<School>
          columns={columns}
          data={data?.schools ?? []}
          rowKey={s => s.id}
          onRowClick={s => setSelected(s)}
          loading={isLoading}
          bordered
          emptyMessage={submitted
            ? `No school matches “${submitted}”.`
            : 'No schools yet. Add the first one.'}
        />
      )}

      <SchoolModal modal={modal} setModal={setModal} save={save} />
    </div>
  );
}

/** Add or rename. Deliberately carries no tenant field — ownership moves only
 *  through Claim, so a rename can never transfer a school. */
function SchoolModal({
  modal, setModal, save,
}: {
  modal: Partial<School> | null;
  setModal: (m: Partial<School> | null) => void;
  save: { mutate: (s: Partial<School>) => void; isPending: boolean; isError: boolean; error: unknown };
}) {
  return (
    <Modal isOpen={!!modal} onClose={() => setModal(null)}
      title={modal?.id ? 'Rename School' : 'New School'} size="sm">
      {modal && (
        <div className="school-form">
          <label>Name
            <Input value={modal.name ?? ''} placeholder="St Xavier's High School"
              onChange={e => setModal({ ...modal, name: e.target.value })} />
          </label>
          <label>City (optional)
            <Input value={modal.city ?? ''} placeholder="Pune"
              onChange={e => setModal({ ...modal, city: e.target.value })} />
          </label>
          {/* PLURAL: a school can run CBSE and ICSE side by side, and forcing
              one misdescribed the school either way. Which board a given CLASS
              follows is set on the class. */}
          <fieldset className="school-boards">
            <legend>Boards offered</legend>
            {REGISTRABLE_PROGRAMME_OPTIONS.map(b => {
              const on = (modal.boards ?? []).includes(b);
              return (
                <label key={b} className={on ? 'is-on' : ''}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setModal({
                      ...modal,
                      boards: on
                        ? (modal.boards ?? []).filter(x => x !== b)
                        : [...(modal.boards ?? []), b],
                    })}
                  />
                  {b}
                </label>
              );
            })}
          </fieldset>
          {modal.id && (
            <p className="school-hint">Renaming does not change who owns this school.</p>
          )}
          {save.isError && <p className="school-error">{(save.error as Error).message}</p>}
          <div className="school-modal-actions">
            <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm"
              disabled={!modal.name?.trim() || save.isPending}
              onClick={() => save.mutate(modal)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
